import React, { useMemo, useState } from "react";
import { Button, Group, Table, Text, TextInput, Title, useMantineColorScheme, Container, Badge, Paper, Pagination, Loader, Center } from "@mantine/core";
import { IconPlus, IconSearch, IconArrowLeft } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import { useListTeamsQuery } from "generated/graphql";

const PAGE_SIZE = 10;

/**
 * TeamManagement (Admin)
 *
 * Lists all teams with search, pagination, and navigation to edit/create.
 * Uses real API data from the ListTeams GraphQL query.
 */
export default function TeamManagement() {
  const navigate = useNavigate();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  const [activePage, setActivePage] = useState(1);
  const [query, setQuery] = useState("");

  const { data, loading, error, refetch } = useListTeamsQuery({
    variables: {
      skip: (activePage - 1) * PAGE_SIZE,
      take: PAGE_SIZE,
    },
    fetchPolicy: "cache-and-network",
  });

  // Map API data to table format
  const teams = useMemo(() => {
    return (data?.teams?.items ?? []).map((t) => ({
      id: t.id,
      name: t.name,
      franchise: t.franchise?.name ?? "—",
      franchisePrefix: t.franchise?.prefix ?? "",
      active: t.active,
      playerCount: t.players?.length ?? 0,
    }));
  }, [data]);

  const totalCount = data?.teams?.totalCount ?? 0;
  const pageCount = Math.ceil(totalCount / PAGE_SIZE);

  // Client-side filtering of current page results
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return teams;
    return teams.filter((t) =>
      [t.name, t.id, t.franchise, t.franchisePrefix].some((v) => v.toLowerCase().includes(q))
    );
  }, [query, teams]);

  function onSelectTeam(id: string) {
    // Navigate to the Team Edit page for the given team id
    navigate(`/admin-hub/teams/${id}`);
  }

  function onCreateTeam() {
    // Open the Team Create page
    navigate(`/admin-hub/teams/create`);
  }

  return (
    <Container fluid px={{ base: "sm", sm: "lg" }} py={{ base: "sm", sm: "lg" }}>
      <Group justify="flex-start" mb="sm">
        <Button
          variant="filled"
          color="blue"
          className="bg-blue-600 hover:bg-blue-700 text-white"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/admin-hub')}
          size="sm"
        >
          Back to Admin Hub
        </Button>
      </Group>
      <div className="space-y-4">
        <Title c={isDark ? "white" : "black"} size="h3" className="sm:text-2xl">Team Management</Title>
        <Text c={isDark ? "gray.4" : "gray.7"} size="sm">
          Manage all teams in the league. Search filters results on the current page.
        </Text>

        <div className="flex flex-col sm:flex-row gap-3 sm:justify-between sm:items-center">
          <TextInput
            placeholder="Search teams by name or franchise"
            leftSection={<IconSearch size={16} />}
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            className="w-full sm:max-w-xs"
          />
          <Button leftSection={<IconPlus />} className="bg-blue-500 hover:bg-blue-600" onClick={onCreateTeam} size="sm">
            Create Team
          </Button>
        </div>

        <Paper withBorder radius="md" shadow="sm" className="overflow-x-auto">
          <Table
            striped
            highlightOnHover
            withTableBorder
            withColumnBorders
            horizontalSpacing="md"
            verticalSpacing="sm"
          >
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Team</Table.Th>
                <Table.Th>Franchise</Table.Th>
                <Table.Th>Players</Table.Th>
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {loading && filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Center py="md">
                      <Loader size="sm" />
                    </Center>
                  </Table.Td>
                </Table.Tr>
              ) : error ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c="red">Failed to load teams.</Text>
                  </Table.Td>
                </Table.Tr>
              ) : filtered.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={4}>
                    <Text size="sm" c={isDark ? "gray.5" : "gray.6"}>
                      {query ? "No teams match your search." : "No teams found."}
                    </Text>
                  </Table.Td>
                </Table.Tr>
              ) : (
                filtered.map((t) => (
                  <Table.Tr
                    key={t.id}
                    onClick={() => onSelectTeam(t.id)}
                    role="button"
                    tabIndex={0}
                    aria-label={`Open team ${t.name}`}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        onSelectTeam(t.id);
                      }
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    <Table.Td>{t.name}</Table.Td>
                    <Table.Td>
                      {t.franchisePrefix ? `[${t.franchisePrefix}] ` : ""}{t.franchise}
                    </Table.Td>
                    <Table.Td>{t.playerCount}</Table.Td>
                    <Table.Td>
                      {t.active ? (
                        <Badge color="green" variant="light">Active</Badge>
                      ) : (
                        <Badge color="red" variant="light">Inactive</Badge>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))
              )}
            </Table.Tbody>
          </Table>
        </Paper>

        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <Text size="sm" c={isDark ? "gray.5" : "gray.6"} className="text-center sm:text-left">
            Showing {filtered.length} of {totalCount} team{totalCount === 1 ? "" : "s"}
          </Text>
          {pageCount > 1 && (
            <Pagination
              total={pageCount}
              value={activePage}
              onChange={(page) => {
                setActivePage(page);
                refetch({
                  skip: (page - 1) * PAGE_SIZE,
                  take: PAGE_SIZE,
                });
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              size="sm"
              className="justify-center sm:justify-end"
            />
          )}
        </div>
      </div>
    </Container>
  );
}
